/**
 * Calendar-day arithmetic for date-only pickers and reports.
 *
 * Deliberately separate from `dateTimeParts.ts`, which manipulates an *instant* (a call's
 * scheduled time) and therefore always carries an hour and a minute. A reporting range has
 * no time of day at all: "August 1 through August 31" is 31 calendar days, and the only
 * thing that decides which instants those cover is the reader's timezone — which the API
 * resolves, not the client. Modelling a range as two instants invites exactly the off-by-a-
 * few-hours bug this type exists to avoid.
 */

/** A calendar day with no time and no zone. `month` is 1-12, matching how a date reads. */
export interface CalendarDate {
  year: number;
  /** 1-12. */
  month: number;
  day: number;
}

/** An inclusive span of calendar days. Either end may be unset while the user is picking. */
export interface CalendarDateRange {
  start: CalendarDate | null;
  end: CalendarDate | null;
}

const pad = (value: number) => String(value).padStart(2, "0");

/** Serialize to the `YYYY-MM-DD` form the API's `startDate`/`endDate` params take. */
export const toIsoDate = (date: CalendarDate): string =>
  `${date.year}-${pad(date.month)}-${pad(date.day)}`;

/** Today in the viewer's own zone — the days a coach is reporting on are their days. */
export const todayCalendarDate = (): CalendarDate => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
};

/** Days in a 1-12 month, leap years included. Day 0 of the next month is this month's last. */
export const daysInCalendarMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

/** Weekday index (0 = Sunday) that a 1-12 month starts on. */
export const firstWeekdayOfCalendarMonth = (year: number, month: number): number =>
  new Date(year, month - 1, 1).getDay();

/** Shift by whole days, rolling across month and year boundaries. */
export const addCalendarDays = (date: CalendarDate, delta: number): CalendarDate => {
  const shifted = new Date(date.year, date.month - 1, date.day + delta);
  return {
    year: shifted.getFullYear(),
    month: shifted.getMonth() + 1,
    day: shifted.getDate(),
  };
};

/**
 * Shift by whole months, clamping the day so Jan 31 going forward lands on Feb 28 rather
 * than rolling into March.
 */
export const addCalendarMonths = (date: CalendarDate, delta: number): CalendarDate => {
  const target = new Date(date.year, date.month - 1 + delta, 1);
  const year = target.getFullYear();
  const month = target.getMonth() + 1;
  return { year, month, day: Math.min(date.day, daysInCalendarMonth(year, month)) };
};

/** Negative when `a` is earlier, 0 when the same day, positive when later. */
export const compareCalendarDates = (a: CalendarDate, b: CalendarDate): number =>
  a.year - b.year || a.month - b.month || a.day - b.day;

export const isSameCalendarDate = (
  a: CalendarDate | null,
  b: CalendarDate | null
): boolean => !!a && !!b && compareCalendarDates(a, b) === 0;

/** True when `date` falls on or between `start` and `end` (either order-safe, both required). */
export const isWithinCalendarRange = (
  date: CalendarDate,
  start: CalendarDate | null,
  end: CalendarDate | null
): boolean => {
  if (!start || !end) return false;
  const [from, to] =
    compareCalendarDates(start, end) <= 0 ? [start, end] : [end, start];
  return compareCalendarDates(date, from) >= 0 && compareCalendarDates(date, to) <= 0;
};

/**
 * Whole days spanned inclusively, so a single day is 1. Order-insensitive, matching
 * `isWithinCalendarRange` — a caller checking a span against a maximum must not get a
 * negative number that silently passes the check.
 *
 * Counted in UTC: a local-time subtraction across a spring-forward weekend is 23 hours
 * short and rounds to one day fewer.
 */
export const calendarDaysBetween = (start: CalendarDate, end: CalendarDate): number => {
  const from = Date.UTC(start.year, start.month - 1, start.day);
  const to = Date.UTC(end.year, end.month - 1, end.day);
  return Math.abs(Math.round((to - from) / 86_400_000)) + 1;
};

/** "Aug 1, 2026". The year is always shown: a compliance range is often not this year. */
export const formatCalendarDate = (date: CalendarDate, locale?: string): string =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(date.year, date.month - 1, date.day));

/** "Aug 1, 2026 - Aug 31, 2026", or just the start while the end is still being picked. */
export const formatCalendarRange = (
  range: CalendarDateRange,
  locale?: string
): string => {
  if (!range.start) return "";
  const start = formatCalendarDate(range.start, locale);
  return range.end ? `${start} - ${formatCalendarDate(range.end, locale)}` : start;
};
